import { type Channel, type Partner, partners } from "@expert/shared-types";

const IS_DEV = import.meta.env.VITE_ENV_TYPE === "development";
const CLOUD_SPECIFIC_SERVICE_PROVIDER = import.meta.env.VITE_CLOUD_SPECIFIC_SERVICE_PROVIDER;

export const SERVICE_PROVIDER_QS_KEY = "serviceProvider";
export const ALLOWED_PARTNER_QS_KEY = "allowedPartner";

export const serviceProviders = Object.freeze(["AMAZON_CONNECT", "TWILIO", "MOCK_PROVIDER"] as const);

export type ServiceProvider = (typeof serviceProviders)[number];

export function isServiceProvider(value: unknown): value is ServiceProvider {
    return typeof value === "string" && (serviceProviders as readonly string[]).includes(value);
}

export function isAllowedPartner(value: unknown): value is Partner {
    return typeof value === "string" && (["No partners", ...partners] as readonly string[]).includes(value);
}

export function assertServiceProvider(value: unknown): asserts value is ServiceProvider {
    if (isServiceProvider(value)) return;
    throw new Error(`value is not a ServiceProvider: ${value?.toString()}`);
}

export function assertAllowedPartner(value: unknown): asserts value is Partner {
    if (isAllowedPartner(value)) return;
    throw new Error(`value is not an allowedPartner: ${value?.toString()}`);
}

export function getServiceProvidersOverride() {
    if (!IS_DEV) return;
    const values = new URLSearchParams(window.location.search).getAll(SERVICE_PROVIDER_QS_KEY);
    if (values.length === 0) return;
    for (const value of values) assertServiceProvider(value);
    return values as ServiceProvider[];
}

export function getAllowedPartnersOverride() {
    if (!IS_DEV) return;
    const values = new URLSearchParams(window.location.search).getAll(ALLOWED_PARTNER_QS_KEY);
    if (values.length === 0) return;
    for (const value of values) assertAllowedPartner(value);
    return values as Partner[];
}

// TODO: allow multi-partner configuration.

/** Partners that support connect for the voice channel */
const AMAZON_CONNECT_VOICE_PARTNERS: Partner[] = [
    "cactus",
    "cricket",
    "mcafee",
    "mcafeesecurityscan",
    "adt",
    "microsoft",
    "tmobile",
    "life360",
    "amazonrouter",
    "youtubetv",
    "ee",
    "verizon",
    "verizonconnectedhome",
    "verizonwholeoffice",
    "vzwsmb",
    "fios",
];

export function getServiceProviders(partner: Partner | undefined, channel: Channel): ServiceProvider[] {
    // serviceProviderOverride should already be undefined in Prod,
    // but leaving the IS_DEV check here for extra safety
    const serviceProviderOverride = getServiceProvidersOverride();
    if (IS_DEV && Array.isArray(serviceProviderOverride)) {
        return serviceProviderOverride;
    }

    if (channel === "chat") {
        //TODO: chat agents aren't grouped strictly by one partner. Determination is needed at the point we auth the agent.
        // Routing profiles in AWS would be most specific to what we are doing

        // /** Partners that support Amazon Connect for chat channels */
        // const AMAZON_CONNECT_CHAT_PARTNERS: Partner[] = [];
        // if (partner && AMAZON_CONNECT_CHAT_PARTNERS.includes(partner)) { return ["AMAZON_CONNECT"]; }
        // If VITE_CLOUD_SPECIFIC_SERVICE_PROVIDER is set, return its value as the service provider
        if (CLOUD_SPECIFIC_SERVICE_PROVIDER) {
            return [CLOUD_SPECIFIC_SERVICE_PROVIDER as ServiceProvider];
        }
        return ["TWILIO"];
    }

    if (partner && AMAZON_CONNECT_VOICE_PARTNERS.includes(partner)) {
        return ["AMAZON_CONNECT"];
    }

    return ["TWILIO"];
}

export function selectPartnerWithOverride(partner: Partner): Partner {
    const partnerOverride = getAllowedPartnersOverride();
    return partnerOverride ? partnerOverride[0] : partner;
}
